import { readBuildMetadata } from '../siteSupport/types';

export interface ActivityHeartbeatPayload {
  screenWidth?: number;
  screenHeight?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  devicePixelRatio?: number;
  timezone?: string;
  locale?: string;
  lastPath?: string;
  appBuild?: string;
  themeFamily?: string;
  themeMode?: string;
  maxTouchPoints?: number;
}

function readThemeFromStorage(storageKey: string): { themeFamily?: string; themeMode?: string } {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as {
      state?: { family?: string; mode?: string };
      family?: string;
      mode?: string;
    };
    const state = parsed.state ?? parsed;
    return {
      themeFamily: typeof state.family === 'string' ? state.family : undefined,
      themeMode: typeof state.mode === 'string' ? state.mode : undefined
    };
  } catch {
    return {};
  }
}

export function collectClientContext(themeStorageKey: string): ActivityHeartbeatPayload {
  if (typeof window === 'undefined') return {};

  const meta = readBuildMetadata();
  const appBuild =
    meta.buildVersion?.trim() ||
    meta.productVersion?.trim() ||
    meta.version?.trim() ||
    undefined;

  const theme = readThemeFromStorage(themeStorageKey);
  const pathname = window.location.pathname;
  const lastPath = pathname.length > 256 ? pathname.slice(0, 256) : pathname;

  const payload: ActivityHeartbeatPayload = {
    screenWidth: window.screen.width > 0 ? window.screen.width : undefined,
    screenHeight: window.screen.height > 0 ? window.screen.height : undefined,
    viewportWidth: window.innerWidth > 0 ? window.innerWidth : undefined,
    viewportHeight: window.innerHeight > 0 ? window.innerHeight : undefined,
    devicePixelRatio:
      typeof window.devicePixelRatio === 'number' && window.devicePixelRatio > 0
        ? window.devicePixelRatio
        : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
    locale: navigator.language || undefined,
    lastPath: lastPath || undefined,
    appBuild,
    themeFamily: theme.themeFamily,
    themeMode: theme.themeMode,
    maxTouchPoints:
      typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints >= 0
        ? navigator.maxTouchPoints
        : undefined
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== '')
  ) as ActivityHeartbeatPayload;
}
